import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import React, { useEffect } from 'react';
import { Button, Icon, Message } from 'semantic-ui-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Form } from 'components/form-elements/Form';
import { LabeledInput } from 'components/form-elements/inputs/LabeledInput';
import { ShouldRender } from 'components/ShouldRender';
import { Grid } from 'components/layout/Grid';
import { Brikk } from 'components/Brikk';
import { Text } from 'components/typography';
import { LabeledElement } from 'components/form-elements/labels/LabeledElement';
import { LabeledTimePicker } from 'components/form-elements/pickers/LabeledTimePicker';
import { LabeledWeekdayPicker } from 'components/form-elements/pickers/LabeledWeekdayPicker';
import { initialTime } from 'components/../utils/timeAndDate';
import { useCreateTimeProfile } from 'services/2n/time-profiles';
import { useUserPermissions } from 'services/user/permissions';
import { useAccessManagement2NModal } from '../../../AccessManagement2NModalContext/useAccessManagement2NModal';
import timeProfileFormSchema from '../TimeProfileForm/validation';
import styles from './styles.module.scss';
import { FormActions } from './components/FormActions';

export const AddTimeProfileForm = () => {
  const { checkPermission } = useUserPermissions();
  const isAdmin = checkPermission('admin-users', 'create');
  const isSystemAdmin = checkPermission('system-admin', 'create');

  const accessManagement2NModal = useAccessManagement2NModal();
  const { mutateAsync: createTimeProfileAsync, isLoading: isCreating } = useCreateTimeProfile();

  const formMethods = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(timeProfileFormSchema),
    defaultValues: {
      name: isAdmin && !isSystemAdmin ? 'GH-' : '',
      timeProfiles: [{ startTime: initialTime, endTime: initialTime, days: [] }],
    },
  });

  const { control, handleSubmit, getValues, watch, formState } = formMethods;

  const { errors } = formState || {};

  const { fields: timeProfileFields, append: appendTimeProfile, remove: removeTimeProfile } = useFieldArray({
    control,
    name: 'timeProfiles',
  });

  const watchedTimeProfiles = watch('timeProfiles');

  const allDaysUsed = (watchedTimeProfiles || [])
    .reduce((acc, profile) => acc.concat((profile && profile.days) || []), []).length >= 7;

  const onSubmit = async (data) => {

    await createTimeProfileAsync({
      name: data.name,
      timeProfiles: data.timeProfiles,
    }, {
      onSuccess: (timeProfile) => {
        accessManagement2NModal.updateSelected('timeProfile', timeProfile);
        accessManagement2NModal.updateAdding('timeProfile', false);
      },
    });
  };

  function onDayClick(index) {
    return (day) => {
      const days = getValues(`timeProfiles.${index}.days`) || [];
      if (days.includes(day)) {
        return days.filter(selectedDay => selectedDay !== day);
      }
      return [...days, day];
    };
  }

  useEffect(() => {
    if (formState.isDirty) {
      accessManagement2NModal.updateSavedChanges('timeProfile', formState.isDirty);
    }
  }, [formState.isDirty]);

  return (
    <div className={styles.formContainer}>
      <FormProvider {...formMethods}>
        <Brikk.Heading heading="addNewTimeProfile" />

        <Form
          id="add-time-profile-form"
          className={styles.form}
          onSubmit={handleSubmit(onSubmit)}
        >

          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <LabeledInput
                labelIntl="timeProfileName"
                id="name"
                value={field.value}
                placeholder={isAdmin && !isSystemAdmin ? 'GH-XXXXXXX' : ''}
                onChange={(event) => {
                  if (isAdmin && !isSystemAdmin) {
                    const inputValue = event.target.value;
                    if (inputValue === '' || inputValue.toUpperCase() === 'GH-') {
                      field.onChange('');
                      return;
                    }
                    if (!inputValue.toUpperCase().startsWith('GH-')) {
                      field.onChange(`GH-${inputValue}`);
                    } else {
                      field.onChange(inputValue);
                    }
                  } else {
                    field.onChange(event.target.value);
                  }
                }}
                hasError={!!(errors && errors.name)}
              />
            )}
          />

          <ShouldRender condition={!!(errors && errors.name)}>
            <Message negative>
              <Message.Content>
                <Text
                  error
                  intl={errors.name && errors.name.message}
                />
              </Message.Content>
            </Message>
          </ShouldRender>

          <LabeledElement label="timeProfile">
            {timeProfileFields.map((profileField, index) => (
              <div
                key={profileField.id}
                className={styles.timeProfileEntry}
              >
                <Grid
                  twoEqualColumns
                  noPadding
                >
                  <Controller
                    name={`timeProfiles.${index}.startTime`}
                    control={control}
                    render={({ field }) => (
                      <LabeledTimePicker
                        title="startTime"
                        value={field.value}
                        onChange={(value) => field.onChange(value)}
                        timeInterval={15}
                        className={styles.timeProfileWrapper}
                        allowManualInput
                      />
                    )}
                  />

                  <ShouldRender condition={errors 
                       && errors.timeProfiles 
                       && errors.timeProfiles[index] 
                       && errors.timeProfiles[index].startTime
                       && errors.timeProfiles[index].startTime.message}
                  >
                    <Message negative>
                      <Message.Content>
                        <Text
                          error
                          intl={errors 
                               && errors.timeProfiles 
                               && errors.timeProfiles[index] 
                               && errors.timeProfiles[index].startTime 
                               && errors.timeProfiles[index].startTime.message}
                        />
                      </Message.Content>
                    </Message>
                  </ShouldRender>

                  <Controller
                    name={`timeProfiles.${index}.endTime`}
                    control={control}
                    render={({ field }) => (
                      <LabeledTimePicker
                        title="endTime"
                        value={field.value}
                        onChange={(value) => field.onChange(value)}
                        timeInterval={15}
                        className={styles.timeProfileWrapper}
                        allowManualInput
                      />
                    )}
                  />
                </Grid>

                <ShouldRender condition={errors 
                     && errors.timeProfiles 
                     && errors.timeProfiles[index] 
                     && errors.timeProfiles[index].endTime 
                     && errors.timeProfiles[index].endTime.message}
                >
                  <Message negative>
                    <Message.Content>
                      <Text
                        error
                        intl={errors 
                             && errors.timeProfiles 
                             && errors.timeProfiles[index] 
                             && errors.timeProfiles[index].endTime 
                             && errors.timeProfiles[index].endTime.message}
                      />
                    </Message.Content>
                  </Message>
                </ShouldRender>

                <Controller
                  name={`timeProfiles.${index}.days`}
                  control={control}
                  render={({ field }) => {
                    const disabledDays = (watchedTimeProfiles || [])
                      .reduce((acc, profile, profileIndex) => {
                        if (profileIndex === index || !(profile && profile.days)) return acc;
                        return acc.concat(profile.days);
                      }, []);

                    return (
                      <>
                        <LabeledWeekdayPicker
                          title="daysOfTheWeek"
                          selectedDays={field.value}
                          disabledDays={disabledDays}
                          onClick={(e) => field.onChange(onDayClick(index)(parseInt(e.target.value, 10)))}
                          className={styles.weekdays}
                        />
                        {(() => {
                          const tpErr = errors && errors.timeProfiles && errors.timeProfiles[index];
                          const daysMsg = tpErr && tpErr.days ? tpErr.days.message : null;
                          return (
                            <ShouldRender condition={!!daysMsg}>
                              <Message negative>
                                <Message.Content>
                                  <Text
                                    error
                                    intl={daysMsg}
                                  />
                                </Message.Content>
                              </Message>
                            </ShouldRender>
                          );
                        })()}
                      </>
                    );
                  }}
                />

                <ShouldRender condition={timeProfileFields.length > 1}>
                  <Button
                    type="button"
                    size="mini"
                    onClick={() => removeTimeProfile(index)}
                    className={styles.removeTimeProfile}
                  >
                    <Icon name="minus" />
                  </Button>
                </ShouldRender>
              </div>
            ))}

            <ShouldRender condition={!allDaysUsed}>
              <Button
                type="button"
                size="mini"
                onClick={() => appendTimeProfile({ startTime: initialTime, endTime: initialTime, days: [] })}
                className={styles.addTimeProfile}
              >
                <Icon name="plus" />
              </Button>
            </ShouldRender>
          </LabeledElement>
        </Form>
        <FormActions
          isLoading={isCreating}
          onSubmit={onSubmit}
        />
      </FormProvider>
    </div>
  );
};
